import type { CalendarEventResponse } from '@troia-v3/types';

type EventDateInput = Pick<CalendarEventResponse, 'startTime'> & { allDay?: boolean };

/**
 * Retorna a data (ano/mês/dia) em que o evento deve ser posicionado no calendário.
 *
 * Eventos all-day são date-only e o canônico é meia-noite UTC (backend e Google
 * usam a PARTE-DATA — `startTime.split('T')[0]`). Se renderizados com
 * `new Date(startTime).getDate()` (local), o dia desloca em fusos negativos
 * (ex.: GMT-3, `2026-07-09T00:00:00.000Z` vira 08/07). Por isso all-day é lido
 * pela parte-data UTC (`slice(0,10)`), independente do fuso do viewer. Eventos
 * com hora continuam no horário local do viewer (comportamento esperado).
 *
 * `month` é 0-indexed para casar com `Date.getMonth()`.
 */
export const getEventDateParts = (
  event: EventDateInput
): { year: number; month: number; day: number } => {
  if (event.allDay) {
    const [year, month, day] = event.startTime.slice(0, 10).split('-').map(Number);
    return { year, month: month - 1, day };
  }
  const d = new Date(event.startTime);
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
};

/** Verdadeiro se o evento cai na data local informada (all-day comparado por data UTC). */
export const eventOccursOnDate = (event: EventDateInput, date: Date): boolean => {
  const p = getEventDateParts(event);
  return (
    p.year === date.getFullYear() &&
    p.month === date.getMonth() &&
    p.day === date.getDate()
  );
};

/** Constrói um `Date` (local, meia-noite) representando o dia do evento — para headers/agrupamento. */
export const getEventLocalDate = (event: EventDateInput): Date => {
  const p = getEventDateParts(event);
  return new Date(p.year, p.month, p.day);
};
